import React, { useMemo, useState } from 'react';
import { Box, Autocomplete, TextField } from '@mui/material';
import { ReactComponent as SelectArrowIcon } from "../../assets/icons/selectArrow.svg";

interface DynamicFormSelectProps {
  label: string;
  error?: boolean;
  helperText?: string;
  // The API types these as strings but sends JSON numbers, so tolerate both.
  menuItems: (string | number)[];
  disabled?: boolean;
  value: string | number | null;
  onChange: (event: any) => void;
  name: string;
  // Shown when the user types something that is not one of menuItems. Without it the
  // entry is silently discarded and the field just goes blank (ID 9).
  noMatchHelperText?: string;
}

const DynamicFormSelect = React.forwardRef<HTMLInputElement, DynamicFormSelectProps>(({
  label,
  error,
  helperText,
  menuItems,
  disabled,
  value,
  onChange,
  name,
  noMatchHelperText,
  ...props
}, ref) => {
  const options = useMemo(() => {
    const stringified = menuItems.map((item) => String(item));

    return stringified;
  }, [menuItems]);

  // What the user last typed, and whether it needs reporting. The Autocomplete is not
  // freeSolo, so unmatched text is thrown away on blur - track it so we can say why.
  const [typedText, setTypedText] = useState('');
  const [showNoMatch, setShowNoMatch] = useState(false);

  const selectedValue = value === null || value === undefined || value === ''
    ? null
    : String(value);

  const clearTyping = () => {
    setTypedText('');
    setShowNoMatch(false);
  };

  const handleInputChange = (_event: unknown, newInputValue: string, reason: string) => {
    if (reason === 'input') {
      setTypedText(newInputValue);
      setShowNoMatch(false);
      return;
    }

    if (reason === 'clear') {
      clearTyping();
      return;
    }

    // 'reset' is MUI reverting the input - on blur, or right after a selection.
    // A selection clears typedText first (see onChange), so anything left here was
    // typed and discarded.
    if (reason === 'reset') {
      const typed = typedText.trim();
      setShowNoMatch(Boolean(typed) && !options.includes(typed));
      setTypedText('');
    }
  };

  const showError = Boolean(error) || (showNoMatch && Boolean(noMatchHelperText));
  const message = error ? helperText : noMatchHelperText;

  return (
    <Box style={{
      width: '100%',
      height: '100%',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      gap: 1,
      display: 'inline-flex',
      position: 'relative',
    }}>
      <Box style={{
        alignSelf: 'stretch',
        marginBottom: 0
      }}>
        <div style={{
          color: disabled ? "#B7B6B6" : '#1A1A1A',
          fontSize: 12,
          fontFamily: 'Helvetica Neue LT Std',
          fontWeight: '400',
          lineHeight: '16px',
          letterSpacing: 0.70,
          wordWrap: 'break-word',
          position: 'absolute',
          top: -7.5,
          left: 10,
          background: disabled ? "transparent" : "white",
          paddingLeft: "3px",
          paddingRight: "3px",
          display: "block",
          zIndex: 1
        }}>
          {label}
        </div>
      </Box>
      <Box style={{
        alignSelf: 'stretch',
        height: 38,
        borderRadius: 4,
        border: '1px #012A52 solid',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        display: 'flex'
      }}>
        <Box style={{
          height: 36,
          width: "100%",
          alignSelf: 'stretch',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8,
          display: 'inline-flex',
          position: 'absolute',
          zIndex: 0,
          left: 0,
        }}>
          <Autocomplete
            options={options}
            value={selectedValue}
            isOptionEqualToValue={(option, selected) => String(option) === String(selected)}
            disabled={disabled}
            onInputChange={handleInputChange}
            renderInput={(params) => {
              if (disabled) {
                params.inputProps.value = '';
              }
              return (
              <TextField
                {...params}
                inputRef={ref}
                name={name}
                error={showError}
                disabled={disabled}
                variant="outlined"
                style={{ border: "none", width: "100%", height: "108%", marginTop: "1px", boxShadow: "none", color: 'black', opacity: disabled ? 0.8 : 1.0, backgroundColor: disabled ? "#f0f0f0" : "transparent"}}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    padding: '0 12px',
                    marginTop: '4px',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '0',
                    fontSize: '16px',
                    fontFamily: 'Roboto',
                    fontWeight: '400',
                  },
                }}
              />
            )}}
            onChange={(_, newValue) => {
              // A real selection - drop any pending typed text so the following
              // 'reset' does not report it as unmatched.
              clearTyping();

              if (!newValue) {
                onChange({ target: { name, value: '' } });
                return;
              }
              const numericValue = parseFloat(newValue);
              onChange({
                target: {
                  name,
                  value: !isNaN(numericValue) ? numericValue : newValue
                }
              });
            }}
            popupIcon={<SelectArrowIcon />}
            sx={{
              width: '100%',
              '& .MuiAutocomplete-endAdornment': {
                right: '12px',
              },
            }}
          />
        </Box>
      </Box>
      {showError && <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{message}</div>}
    </Box>
  );
});

export default DynamicFormSelect;